import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/site";
import { useLang } from "@/lib/lang";
import { GoldDiamond } from "@/components/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1634625828197-d738147871dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxQdWdsaWElMjBzYWxlbnRvJTIwZHVuZXMlMjBiZWFjaCUyMHNlYXxlbnwwfHx8fDE3OTA3OTE1MDN8MA&ixlib=rb-4.1.0&q=85";

const lineVariants = {
  hidden: { y: "112%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.15, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.18 },
  }),
};

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section data-testid="hero-section" ref={ref} id="top" className="noise relative flex min-h-[100svh] flex-col overflow-hidden bg-navy-deep">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-24" aria-hidden="true">
        <img
          src={HERO_IMG}
          alt=""
          className="h-full w-full scale-110 object-cover opacity-45"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/80 via-navy/70 to-navy-deep" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/80 via-transparent to-navy-deep/40" />
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 mx-auto flex w-full max-w-[1400px] flex-1 flex-col justify-center px-6 pt-32 pb-24 lg:px-12"
      >
        <motion.p
          data-testid="hero-overline"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mb-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-gold"
        >
          <span className="inline-block h-px w-10 bg-gold"></span>
          {t.hero.overline}
        </motion.p>

        <h1 data-testid="hero-title" className="max-w-5xl font-serif text-[13vw] leading-[1.02] font-medium tracking-tight text-alabaster sm:text-6xl lg:text-8xl">
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0} variants={lineVariants} initial="hidden" animate="visible" className="block">
              {t.hero.line1}
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span custom={1} variants={lineVariants} initial="hidden" animate="visible" className="block font-light italic text-gold">
              {t.hero.line2}
            </motion.span>
          </span>
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-base leading-relaxed text-alabaster/70 sm:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-cta-consultation"
            onClick={() => scrollToId("contatti")}
            className="group relative overflow-hidden bg-gold px-9 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-navy transition-all duration-500 hover:shadow-[0_18px_40px_-12px_rgba(245,217,63,0.45)]"
          >
            <span className="relative z-10 transition-colors duration-500 group-hover:text-gold">{t.hero.cta1}</span>
            <span className="absolute inset-0 -translate-x-full bg-navy transition-transform duration-500 ease-out group-hover:translate-x-0"></span>
          </button>
          <button
            data-testid="hero-cta-practice-areas"
            onClick={() => scrollToId("aree")}
            className="group flex items-center gap-3 border border-alabaster/25 px-9 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-alabaster transition-all duration-500 hover:border-gold hover:text-gold"
          >
            {t.hero.cta2}
            <ArrowDown size={13} className="transition-transform duration-500 group-hover:translate-y-1" strokeWidth={1.5} />
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.6 }}
        data-testid="hero-stat-badge"
        className="relative z-10 mx-auto flex w-full max-w-[1400px] flex-wrap items-center gap-x-6 gap-y-2 border-t border-alabaster/12 px-6 py-6 font-mono text-[10px] uppercase tracking-[0.24em] text-alabaster/50 lg:px-12"
      >
        {t.hero.badges.map((b) => (
          <span key={b} className="flex items-center gap-6">
            {b}
            <GoldDiamond />
          </span>
        ))}
        <span className="ml-auto hidden items-center gap-2 sm:flex">
          {t.hero.scroll}
          <ArrowDown size={11} className="animate-bounce" strokeWidth={1.5} />
        </span>
      </motion.div>
    </section>
  );
}
